import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { myAccountsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Building, Search, ChevronDown, ChevronRight, Users, AlertTriangle,
  ClipboardCheck, Clock, ShieldCheck,
} from 'lucide-react';

const STATUS_LABELS = {
  draft: 'Draft',
  awaiting_employee: 'Awaiting employee',
  awaiting_supervisor: 'Awaiting supervisor',
  awaiting_reviewer: 'Awaiting reviewer',
  in_review: 'In review',
  pending_reviewer: 'Pending reviewer',
  pending_hr: 'Pending HR',
};

const TONES = {
  slate: 'bg-slate-100 text-slate-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-600',
};

const Stat = ({ label, value, icon: Icon, tone = 'slate', testId }) => (
  <div className="flex items-center gap-3 px-5 py-4" data-testid={testId}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TONES[tone]}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <p className="text-2xl font-semibold leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  </div>
);

const DueBadge = ({ row }) => (
  <Badge
    variant={row.is_overdue ? 'destructive' : 'secondary'}
    className="mr-1 mb-1"
    data-testid={`due-badge-${row.evaluation_type.replace(/\s+/g, '-').toLowerCase()}`}
  >
    {row.evaluation_type} · {row.is_overdue
      ? `${Math.abs(row.days_until)}d overdue`
      : `due in ${row.days_until}d`}
  </Badge>
);

export default function MyAccounts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [peopleByClient, setPeopleByClient] = useState({});
  const [loadingPeople, setLoadingPeople] = useState({});
  const [dueOnly, setDueOnly] = useState(false);

  useEffect(() => {
    myAccountsAPI.list()
      .then(r => setData(r.data))
      .catch(() => setData({ accounts: [], totals: {}, has_employee_record: true }))
      .finally(() => setLoading(false));
  }, []);

  const accounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.accounts || []).filter(a => {
      if (dueOnly && !a.due_count) return false;
      if (!term) return true;
      return (
        a.name.toLowerCase().includes(term) ||
        (a.customer_number || '').toLowerCase().includes(term)
      );
    });
  }, [data, search, dueOnly]);

  const totals = data?.totals || {};

  // People load per account on expand — an admin covering 188 accounts would
  // otherwise pull the entire roster and every evaluation to render this page.
  const toggle = (clientId) => {
    setExpanded(prev => ({ ...prev, [clientId]: !prev[clientId] }));
    if (peopleByClient[clientId] || loadingPeople[clientId]) return;
    setLoadingPeople(prev => ({ ...prev, [clientId]: true }));
    myAccountsAPI.people(clientId)
      .then(r => setPeopleByClient(prev => ({ ...prev, [clientId]: r.data.people || [] })))
      .catch(() => setPeopleByClient(prev => ({ ...prev, [clientId]: [] })))
      .finally(() => setLoadingPeople(prev => ({ ...prev, [clientId]: false })));
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="my-accounts-loading">
        {[...Array(4)].map((_, i) => <div key={i} className="h-20 skeleton-pulse rounded" />)}
      </div>
    );
  }

  const noCoverage = (data?.accounts || []).length === 0;

  return (
    <div className="space-y-6" data-testid="my-accounts-page">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Building className="w-6 h-6 text-slate-500" /> My Accounts
          </h1>
          <p className="text-slate-500 mt-1">
            {data?.full_access
              ? 'Every active client account, with their people and the evaluations due.'
              : 'The client accounts you supervise, with your people and the evaluations due.'}
          </p>
        </div>
      </div>

      {noCoverage ? (
        <Card data-testid="my-accounts-empty">
          <CardContent className="py-14 text-center max-w-xl mx-auto">
            <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">No client accounts are assigned to you</p>
            <p className="text-sm text-slate-500 mt-3">
              {data?.has_employee_record === false
                ? `We couldn't match your sign-in (${user?.email || 'your account'}) to an employee record, so no accounts could be resolved. Ask an administrator to link your user to your employee record.`
                : 'Your access is scoped to the client accounts you supervise. Once an administrator adds you to a Supervisory Group and attaches that group to your client accounts, they will appear here with their people and evaluations.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0 divide-y sm:divide-y-0 sm:divide-x sm:grid sm:grid-cols-4">
              <Stat label="Accounts" value={totals.accounts || 0} icon={Building} testId="stat-accounts" />
              <Stat label="People" value={totals.people || 0} icon={Users} testId="stat-people" />
              <Stat label="Evaluations due" value={totals.due || 0} icon={ClipboardCheck} tone="amber" testId="stat-due" />
              <Stat label="Overdue" value={totals.overdue || 0} icon={AlertTriangle} tone="red" testId="stat-overdue" />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search accounts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="my-accounts-search"
                />
              </div>
              <Button
                variant={dueOnly ? 'default' : 'outline'}
                onClick={() => setDueOnly(v => !v)}
                data-testid="my-accounts-due-only-toggle"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                {dueOnly ? 'Showing accounts with work due' : 'Only accounts with work due'}
              </Button>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {accounts.map(acct => {
              const open = !!expanded[acct.client_id];
              return (
                <Card key={acct.client_id} data-testid={`account-card-${acct.client_id}`}>
                  <button
                    type="button"
                    onClick={() => toggle(acct.client_id)}
                    className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                    data-testid={`account-toggle-${acct.client_id}`}
                  >
                    {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {acct.name}
                        {acct.customer_number && <span className="text-slate-400 font-normal"> · {acct.customer_number}</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {[acct.city, acct.state].filter(Boolean).join(', ') || 'No location on file'}
                        {acct.my_level_labels?.length > 0 && ` · You: ${acct.my_level_labels.join(', ')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" data-testid={`account-people-${acct.client_id}`}>
                        {acct.people_count} {acct.people_count === 1 ? 'person' : 'people'}
                      </Badge>
                      {acct.in_progress_count > 0 && (
                        <Badge variant="secondary" data-testid={`account-inprogress-${acct.client_id}`}>
                          <Clock className="w-3 h-3 mr-1" />{acct.in_progress_count} in progress
                        </Badge>
                      )}
                      {acct.due_count > 0 && (
                        <Badge variant={acct.overdue_count > 0 ? 'destructive' : 'default'} data-testid={`account-due-${acct.client_id}`}>
                          {acct.due_count} due{acct.overdue_count > 0 ? ` · ${acct.overdue_count} overdue` : ''}
                        </Badge>
                      )}
                    </div>
                  </button>

                  {open && (
                    <div className="border-t" data-testid={`account-people-table-${acct.client_id}`}>
                      {loadingPeople[acct.client_id] ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-500" data-testid={`account-people-loading-${acct.client_id}`}>
                          Loading people…
                        </p>
                      ) : (peopleByClient[acct.client_id] || []).length === 0 ? (
                        <p className="px-5 py-8 text-center text-sm text-slate-500">
                          No active employees are assigned to this account yet.
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee</TableHead>
                              <TableHead>Position</TableHead>
                              <TableHead>Hired</TableHead>
                              <TableHead>Last eval</TableHead>
                              <TableHead>Evaluations due</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(peopleByClient[acct.client_id] || []).map(p => (
                              <TableRow
                                key={p.id}
                                className="cursor-pointer"
                                onClick={() => navigate(`/employees/${p.id}`)}
                                data-testid={`account-person-${p.id}`}
                              >
                                <TableCell>
                                  <p className="font-medium">{p.name}</p>
                                  <p className="text-xs text-slate-500 font-mono">{p.employee_number || '-'}</p>
                                </TableCell>
                                <TableCell className="text-sm">{p.position || '-'}</TableCell>
                                <TableCell className="text-sm">{p.hire_date || '-'}</TableCell>
                                <TableCell className="text-sm">{p.last_evaluation_date || '—'}</TableCell>
                                <TableCell>
                                  {p.open_evaluation && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); navigate(`/evaluations/${p.open_evaluation.id}`); }}
                                      className="inline-flex mr-1 mb-1"
                                      data-testid={`open-eval-${p.id}`}
                                    >
                                      <Badge variant="secondary">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {p.open_evaluation.evaluation_type} · {STATUS_LABELS[p.open_evaluation.status] || p.open_evaluation.status}
                                      </Badge>
                                    </button>
                                  )}
                                  {p.due.map(row => <DueBadge key={row.evaluation_type} row={row} />)}
                                  {!p.open_evaluation && p.due.length === 0 && (
                                    <span className="text-sm text-slate-400">Nothing due</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
            {accounts.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center text-slate-500" data-testid="my-accounts-no-matches">
                  <Building className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No accounts match your filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
